import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Aviso, Botao, Cartao, Selo } from '../componentes/ui';
import { requisicaoPublica } from '../servicos/http';
import { formatarCompetencia } from '../servicos/formato';
import { ErroDaApi } from './ErroDaApi';

type TipoDeAcao =
  | 'APROVA_CREDITO'
  | 'UPLOAD_CERTIFICADO_TRANSF_MES'
  | 'UPLOAD_CERTIFICADO_APROPRIACAO_MES'
  | string;

interface AcaoDisponivel {
  readonly emailId: string;
  readonly acao: TipoDeAcao | null;
  readonly competencia: string | null;
  readonly ano: number;
  readonly clienteId: string;
  readonly exigeArquivo: boolean;
  readonly jaRespondida: boolean;
}

/**
 * RN-103, RN-104 — A página que o link do e-mail abre.
 *
 * ## Sem login, por desenho
 *
 * Quem responde é o decisor da empresa — o contador do contribuinte, o
 * responsável do fornecedor — e ele frequentemente **não tem conta**. Exigir
 * login aqui quebraria o fluxo de que a operação depende: o e-mail chega, a
 * pessoa clica, responde, acabou.
 *
 * A autorização é o token da URL: 256 bits, com prazo e uso único. Diferente do
 * legado, que usava a chave primária da linha do cronograma como se fosse
 * segredo — um id que vaza em log, em resposta de API e em exportação.
 *
 * ## Uma tela sem menu
 *
 * Nada de casca do app, nada de navegação. Quem chega aqui veio de um e-mail
 * para fazer uma coisa; oferecer o resto do sistema a uma pessoa sem sessão
 * seria oferecer telas que vão todas dar em "entre primeiro".
 */
export function AcaoPorLink(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [aprovacao, setAprovacao] = useState<boolean | null>(null);

  const acao = useQuery({
    queryKey: ['acao-por-link', token],
    queryFn: () => requisicaoPublica<AcaoDisponivel>(`/icms/acoes/${token ?? ''}`),
    // Um link inválido não melhora com insistência, e cada tentativa gasta o
    // rate limit que protege o endereço de varredura de token.
    retry: false,
  });

  const responder = useMutation({
    mutationFn: async () => {
      const arquivos =
        arquivo === null
          ? []
          : [
              {
                nome: arquivo.name,
                conteudoBase64: await lerComoBase64(arquivo),
                ...(arquivo.type === '' ? {} : { tipoConteudo: arquivo.type }),
              },
            ];

      return requisicaoPublica<{ efeitosAplicados: readonly string[] }>(
        `/icms/acoes/${token ?? ''}`,
        {
          metodo: 'POST',
          corpo: {
            acao: acao.data?.acao,
            ...(aprovacao === null ? {} : { aprovacao }),
            arquivos,
          },
        },
      );
    },
  });

  return (
    <main className="mx-auto flex w-full max-w-2xl flex-col gap-6 px-4 py-12 sm:px-8">
      <header className="flex flex-col gap-1">
        <p className="font-display text-xl font-semibold tracking-tight">Deca</p>
        <p className="text-sm text-conteudo-secundario">MS3 Capital · Créditos de ICMS</p>
      </header>

      {acao.isPending && <p className="text-sm text-conteudo-sutil">Abrindo o link…</p>}

      {acao.isError && (
        <Cartao como="section" titulo="Este link não está mais válido">
          <p className="text-sm text-conteudo-secundario">
            Ele pode ter expirado, já ter sido usado ou estar incompleto — alguns programas de
            e-mail quebram a linha da URL. Se ainda precisa responder, peça um novo à MS3 Capital.
          </p>
        </Cartao>
      )}

      {acao.data !== undefined && (
        <Cartao
          como="section"
          titulo={TITULO[acao.data.acao ?? ''] ?? 'Responder solicitação'}
          subtitulo={
            acao.data.competencia === null
              ? `Exercício ${acao.data.ano}`
              : `Competência ${formatarCompetencia(acao.data.competencia)}`
          }
        >
          {acao.data.jaRespondida ? (
            <Aviso tom="informacao" titulo="Já respondida">
              Esta solicitação já foi respondida. Não é preciso fazer nada — se acha que houve
              engano, fale com a MS3 Capital.
            </Aviso>
          ) : responder.isSuccess ? (
            <Aviso tom="positivo" titulo="Recebido, obrigado">
              A resposta foi registrada e o time da MS3 Capital já a enxerga. Pode fechar esta
              página.
            </Aviso>
          ) : acao.data.acao === null ? (
            <Aviso tom="atencao" titulo="Nada a fazer por aqui">
              Este link não permite nenhuma ação. Se você recebeu um pedido por e-mail, responda a
              mensagem.
            </Aviso>
          ) : (
            <form
              className="flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                responder.mutate();
              }}
            >
              <p className="text-sm text-conteudo-secundario">
                {DESCRICAO[acao.data.acao] ?? 'Confirme abaixo para registrar sua resposta.'}
              </p>

              {acao.data.acao === 'APROVA_CREDITO' && (
                <div className="flex flex-wrap gap-3">
                  <Botao
                    type="button"
                    variante={aprovacao === true ? 'primario' : 'secundario'}
                    onClick={() => setAprovacao(true)}
                  >
                    Aprovo
                  </Botao>
                  <Botao
                    type="button"
                    variante={aprovacao === false ? 'perigo' : 'secundario'}
                    onClick={() => setAprovacao(false)}
                  >
                    Não aprovo
                  </Botao>
                </div>
              )}

              {acao.data.exigeArquivo && (
                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-conteudo-primario">
                    Documento (PDF ou imagem)
                  </span>
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.webp"
                    onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
                    className="text-sm text-conteudo-secundario file:mr-3 file:rounded-card file:border file:border-borda file:bg-superficie-base file:px-3 file:py-2 file:text-sm"
                  />
                  {arquivo !== null && (
                    <Selo tom="informativo" tamanho="pequeno">
                      {arquivo.name}
                    </Selo>
                  )}
                </label>
              )}

              {responder.isError && (
                <ErroDaApi titulo="Não foi possível registrar" erro={responder.error} />
              )}

              <div>
                <Botao
                  type="submit"
                  carregando={responder.isPending}
                  disabled={
                    (acao.data.exigeArquivo && arquivo === null) ||
                    (acao.data.acao === 'APROVA_CREDITO' && aprovacao === null)
                  }
                >
                  Enviar resposta
                </Botao>
              </div>
            </form>
          )}
        </Cartao>
      )}

      <p className="text-center text-xs text-conteudo-sutil">
        Recebeu este link por engano? Ignore esta página — nada é registrado sem o envio.
      </p>
    </main>
  );
}

/* ─── Textos por tipo de ação ─────────────────────────────────────────────── */

const TITULO: Readonly<Record<string, string>> = {
  APROVA_CREDITO: 'Aprovação do crédito do mês',
  UPLOAD_CERTIFICADO_TRANSF_MES: 'Certificado de transferência do crédito',
  UPLOAD_CERTIFICADO_APROPRIACAO_MES: 'Certificado de apropriação do crédito',
};

const DESCRICAO: Readonly<Record<string, string>> = {
  APROVA_CREDITO:
    'Confirme se o crédito calculado para esta competência está aprovado. A resposta fica registrada com data e origem.',
  UPLOAD_CERTIFICADO_TRANSF_MES:
    'Anexe o certificado de transferência emitido para esta competência. Ele fica guardado junto da operação do mês.',
  UPLOAD_CERTIFICADO_APROPRIACAO_MES:
    'Anexe o certificado de apropriação desta competência. Com ele, o mês é marcado como aproveitado.',
};

/** `FileReader` devolve `data:...;base64,XXX` — só o depois da vírgula interessa. */
async function lerComoBase64(arquivo: File): Promise<string> {
  const leitor = new FileReader();
  return new Promise((resolver, rejeitar) => {
    leitor.onload = () => {
      const resultado = String(leitor.result);
      resolver(resultado.slice(resultado.indexOf(',') + 1));
    };
    leitor.onerror = () => rejeitar(new Error('Não foi possível ler o arquivo.'));
    leitor.readAsDataURL(arquivo);
  });
}
